/**
 * The frame every signed-in page renders inside: a left rail, a topbar, and
 * the content column.
 *
 * The design's 1a rail (screen digest in the plan): the `learn.` wordmark, a
 * **Browse catalogue** button, a *Learn* group everybody gets, and a *Teach*
 * group shown only to `me.canTeach`.
 *
 * ── Why the rail is filtered rather than disabled ───────────────────────────
 * Same reasoning as website-enterprise's `Shell.tsx`, which this is read from:
 * a member who cannot teach has no path to becoming one from inside the app —
 * that is an admin's decision on the People page — so the Teach group is
 * absent rather than greyed out. A disabled row promises something is
 * available under conditions, and there are none here.
 *
 * ── Why a rail, when the design calls it a sidebar ──────────────────────────
 * "Rail" throughout this codebase and "sidebar" in the design brief name the
 * same 264px column; this file uses the suite's word.
 */

import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { PRESENCE_LABEL, PRESENCE_STATES, isSettablePresence, type Presence } from '@shared/presence';
import { setPresence } from '@shared/presence-client';
import { api, learn } from '../api';
import { Bell } from './Bell';
import { useAssignedCount, setAssignedCount } from '../counts';
import { ds } from '../ds';
import { PresenceDot, usePresence } from './Presence';
import { Link, useRouter } from '../router';
import { useApp, type Me } from '../state';
import { HAIR, LAYOUT, RADIUS, SHADOW, accountOrigin, mono, monoPlain } from '../theme';
import { Wordmark } from './Wordmark';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** The rail's one count, on **My learning** — see counts.ts. */
  count?: boolean;
}

const LEARN: NavItem[] = [
  { to: '/', label: 'Home', icon: 'Home' },
  { to: '/learning', label: 'My learning', icon: 'BookOpen', count: true },
  { to: '/bundles', label: 'Bundles', icon: 'Layers' },
  { to: '/catalogue', label: 'Catalogue', icon: 'Compass' }
];

const TEACH: NavItem[] = [
  { to: '/teach/courses', label: 'Courses', icon: 'GraduationCap' },
  { to: '/teach/reports', label: 'Reports', icon: 'BarChart2' },
  { to: '/teach/people', label: 'People', icon: 'Users' },
  { to: '/teach/guests', label: 'Guests', icon: 'UserPlus' }
];

/** `/` matches exactly; everything else matches its own subtree. */
const isActive = (to: string, path: string): boolean =>
  to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);

export function Shell({ children }: { children: React.ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = useCallback(() => setNavOpen(false), []);

  // The drawer is fixed over the page, so the page behind it must not scroll —
  // otherwise the content slides under an open rail and the rail looks stuck
  // to the screen rather than open over it.
  useEffect(() => {
    if (!navOpen) return;

    document.body.classList.add('mv-locked');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeNav();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.classList.remove('mv-locked');
      window.removeEventListener('keydown', onKey);
    };
  }, [navOpen, closeNav]);

  // Rotating a phone to landscape can cross the breakpoint with the drawer
  // open, which would leave the page locked behind a rail that is now a
  // static column.
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 901px)');
    const onChange = () => {
      if (wide.matches) closeNav();
    };
    wide.addEventListener('change', onChange);
    return () => wide.removeEventListener('change', onChange);
  }, [closeNav]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      <Topbar onToggleNav={() => setNavOpen((v) => !v)} navOpen={navOpen} />

      <div
        style={{
          maxWidth: 1360,
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 32
        }}
      >
        <Rail open={navOpen} onNavigate={closeNav} />

        {navOpen && (
          <button
            type="button"
            className="mv-nav-scrim"
            aria-label="Close the sections menu"
            onClick={closeNav}
          />
        )}

        <main
          style={{
            flex: 1,
            minWidth: 0,
            padding: LAYOUT.contentPad,
            display: 'grid',
            gap: LAYOUT.stackGap,
            alignContent: 'start'
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

function Topbar({ onToggleNav, navOpen }: { onToggleNav: () => void; navOpen: boolean }) {
  const { IconButton, Input } = ds();
  const { me, theme, setTheme } = useApp();
  const { navigate, query } = useRouter();
  const [term, setTerm] = useState(() => query.get('q') ?? '');

  return (
    <header
      style={{
        height: LAYOUT.topbar,
        background: 'var(--surface-card)',
        borderBottom: '1px solid var(--border-subtle)',
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}
    >
      <div
        style={{
          maxWidth: 1360,
          margin: '0 auto',
          height: '100%',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 16
        }}
      >
        <span className="mv-nav-toggle">
          <IconButton
            icon={navOpen ? 'X' : 'Menu'}
            label={navOpen ? 'Hide sections' : 'Show sections'}
            onClick={onToggleNav}
          />
        </span>

        <Link to="/">
          <Wordmark showMark />
        </Link>

        {/* Enter searches. There is no live-as-you-type search: every query is
            a server-side read over encrypted titles, and one per keystroke
            would be one decryption pass per keystroke. */}
        <div
          style={{ flex: 1, maxWidth: 380, marginLeft: 20, visibility: me?.external ? 'hidden' : 'visible' }}
          className="mv-hide-narrow"
        >
          <Input
            placeholder="Search courses, bundles, people"
            icon="Search"
            size="sm"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !term.trim()) return;
              navigate(`/search?q=${encodeURIComponent(term.trim())}`);
            }}
          />
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Bell />
          <IconButton
            icon={theme === 'dark' ? 'Sun' : 'Moon'}
            label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          />
          {me && (me.external ? <ExternalMenu me={me} /> : <AccountMenu me={me} />)}
        </div>
      </div>
    </header>
  );
}

/** The chip's own background, and so the colour the dot cuts itself out of. */
const CHIP = 'var(--surface-sunken)';

const SETTABLE = PRESENCE_STATES.filter(isSettablePresence);

const menuRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '8px 10px',
  border: 'none',
  borderRadius: RADIUS.control,
  background: 'transparent',
  color: 'var(--text-strong)',
  fontFamily: 'inherit',
  fontSize: 13.5,
  textAlign: 'left',
  cursor: 'pointer'
};

/**
 * The account chip, and the menu its chevron has always promised.
 *
 * The account still lives on the main site: the first row leaves the SPA, which
 * is where the whole chip used to go. What is here besides is presence, which
 * has no page of its own in Learn — without this, saying you are busy would
 * mean a trip to mauvely.com.
 *
 * The dot is on this avatar and on no other. It is the signed-in person's own
 * status, and drawing it on a learner in a report would claim Learn knows
 * something about them that it does not.
 */
function AccountMenu({ me }: { me: Me }) {
  const { Icon } = ds();
  const [open, setOpen] = useState(false);
  const { presence, override } = usePresence();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const choose = (next: Presence | null) => {
    void setPresence(next);
    setOpen(false);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          borderRadius: RADIUS.pill,
          border: 'none',
          background: CHIP,
          color: 'inherit',
          font: 'inherit',
          cursor: 'pointer'
        }}
      >
        {/* `PresenceDot` is `position: absolute` and needs something to be
            absolute inside. The wrapper is here rather than inside `Avatar`
            because `Avatar` also draws other people. */}
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <Avatar name={me.user.name} email={me.user.email} src={me.user.avatarUrl} />
          <PresenceDot presence={presence} size={8} ring={CHIP} />
        </span>
        <span style={{ ...monoPlain, letterSpacing: 0, textTransform: 'none' }}>
          {me.user.name}
        </span>
        <Icon name="ChevronDown" size={13} />
      </button>

      {open && (
        <>
          <button
            type="button"
            className="mv-scrim"
            aria-label="Close account menu"
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 8,
              zIndex: 41,
              width: 226,
              padding: 8,
              display: 'grid',
              gap: 2,
              alignContent: 'start',
              background: 'var(--surface-card)',
              border: HAIR,
              borderRadius: RADIUS.card,
              boxShadow: SHADOW.popover
            }}
          >
            <a
              href={`${accountOrigin()}/account`}
              style={menuRow}
              className="mv-row"
            >
              <Icon name="User" size={14} />
              Account
            </a>

            <div style={{ borderTop: HAIR, margin: '4px 2px' }} />

            {SETTABLE.map((choice) => (
              <button
                key={choice}
                type="button"
                className="mv-row"
                onClick={() => choose(choice)}
                style={menuRow}
              >
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    width: 10,
                    height: 10,
                    flex: 'none'
                  }}
                >
                  <PresenceDot presence={choice} size={10} ring="var(--surface-card)" />
                </span>
                {PRESENCE_LABEL[choice]}
                {override === choice && (
                  <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                    <Icon name="Check" size={14} />
                  </span>
                )}
              </button>
            ))}

            {override && (
              <button
                type="button"
                className="mv-row"
                onClick={() => choose(null)}
                style={{ ...menuRow, color: 'var(--text-muted)' }}
              >
                <span style={{ width: 10, flex: 'none' }} />
                Clear
              </button>
            )}
          </div>
        </>
      )}
    </span>
  );
}

/**
 * The chip for somebody on a magic link: their name, and a way out. No
 * account to go to and no presence to set — they have neither.
 */
function ExternalMenu({ me }: { me: Me }) {
  const { Button } = ds();
  const signOut = async () => {
    await api.externalSignOut().catch(() => undefined);
    window.location.href = '/';
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <span style={{ ...monoPlain, letterSpacing: 0, textTransform: 'none' }}>{me.user.name}</span>
      <Button variant="ghost" size="sm" onClick={() => void signOut()}>
        Sign out
      </Button>
    </span>
  );
}

/**
 * The account's photo, or its initials.
 *
 * The wire has carried `avatarUrl` since this app had a session — the same URL
 * Cloud draws, built by `worker/lib/session.ts` from `avatar_key` — and nothing
 * here used it, so every account in Learn was a generic person glyph.
 *
 * ── The initials are the answer, not a placeholder ──────────────────────────
 * Most accounts have no photo. The chip below is what those accounts look like
 * and it is finished; it is not a spinner waiting for an image.
 *
 * ── A photo that fails to load leaves the circle empty ──────────────────────
 * Deliberately, and copied from Cloud along with its reasoning: falling back to
 * initials would make a 404 look exactly like an account with no photo, and
 * "the avatar doesn't show" is then a bug with no symptom. The console line is
 * the symptom.
 */
function Avatar({
  name,
  email,
  size = 22,
  src
}: {
  name: string;
  email: string;
  size?: number;
  src?: string | null;
}) {
  const shape = {
    width: size,
    height: size,
    flex: `0 0 ${size}px`,
    borderRadius: '50%'
  } as const;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ ...shape, objectFit: 'cover' }}
        onError={() => {
          console.warn(
            `[avatar] ${src} did not load. The account has an avatar_key, so ` +
              'this is website-main not serving it — check that MEDIA is bound ' +
              'on that deployment, and that this origin is in img-src.'
          );
        }}
      />
    );
  }

  return (
    <span
      style={{
        ...shape,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--surface-raised)',
        color: 'var(--text-body)',
        fontSize: size * 0.4,
        fontWeight: 600
      }}
    >
      {initials(name, email)}
    </span>
  );
}

/** Up to two letters. The email is the fallback for an account with no name,
 *  which is every account provisioned by an organisation before somebody has
 *  signed in and set one. */
function initials(name: string, email: string): string {
  const source = name.trim() || email.trim();
  if (!source) return '?';
  const words = source.split(/[\s@._-]+/u).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}


function Rail({ open, onNavigate }: { open: boolean; onNavigate: () => void }) {
  const { Icon, Button } = ds();
  const { path, navigate } = useRouter();
  const { me } = useApp();
  const assigned = useAssignedCount();

  // Home and Learning publish this count as they load — see counts.ts — so a
  // session that opens straight into the catalogue, a course, search or a
  // Teach screen, and never either of those two, would otherwise carry no
  // number for the whole session. This is the one other place that fetches
  // for it — skipped on Home and Learning themselves so a landing there does
  // not fire the same request twice — and only until something already has.
  useEffect(() => {
    if (assigned !== null || path === '/' || path === '/learning') return;
    let cancelled = false;
    void learn
      .learning()
      .then((data) => {
        if (!cancelled) setAssignedCount(data.counts.assigned);
      })
      .catch(() => {
        // Best effort — the row just stays without a number, as it would have
        // before this page loaded at all.
      });
    return () => {
      cancelled = true;
    };
  }, [assigned, path]);

  // Somebody on a magic link browses nothing: no catalogue, no search.
  const learnItems = me?.external ? LEARN.filter((i) => i.to !== '/catalogue') : LEARN;
  const groups = [
    { heading: 'Learn', items: learnItems },
    ...(me?.canTeach ? [{ heading: 'Teach', items: TEACH }] : [])
  ];

  return (
    // Layout entirely in styles.css, and deliberately not inline — see the
    // file header for why an inline `display: grid` would win the drawer's
    // media query and leave it permanently open or permanently collapsed.
    <nav className={`mv-nav${open ? ' mv-nav-open' : ''}`}>
      {!me?.external && (
        <div style={{ padding: '0 10px 4px' }}>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => {
              onNavigate();
              navigate('/catalogue');
            }}
          >
            Browse catalogue
          </Button>
        </div>
      )}

      {groups.map((group) => (
        <div key={group.heading} style={{ display: 'grid', gap: 3 }}>
          <span style={{ ...mono, padding: '0 10px 5px' }}>{group.heading}</span>
          {group.items.map((item) => {
            const active = isActive(item.to, path);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  height: LAYOUT.navRow,
                  padding: '0 10px',
                  borderRadius: RADIUS.control,
                  fontSize: 13.5,
                  fontWeight: active ? 600 : 500,
                  background: active ? 'var(--primary-soft)' : 'transparent',
                  color: active ? 'var(--primary-text)' : 'var(--text-muted)'
                }}
              >
                <Icon name={item.icon} size={15} />
                {item.label}
                {item.count && assigned !== null && (
                  <span style={{ ...monoPlain, marginLeft: 'auto' }}>{assigned}</span>
                )}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
